import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DataTable from "../../components/admin/DataTable";

const blank = {
  code: "", description: "", discount_type: "percent", value: 10,
  max_discount: null, min_order: 0, usage_limit: null, per_user_limit: 1,
  applicable_category_ids: [], applicable_seller_ids: [], applicable_product_ids: [],
  first_order_only: false, new_user_only: false, start_at: "", end_at: "", is_active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Fetch tenant products once for the "Eligible products" picker.
  // 200 is plenty for a coupon builder; if a merchant has more they
  // can filter via the search box before picking.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/products", { params: { limit: 200 } });
        setProducts(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* products list is optional — coupon can still apply to everything */ }
    })();
  }, []);

  const visibleProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products.slice(0, 40);
    return products
      .filter((p) => (p.name || p.title || "").toLowerCase().includes(q))
      .slice(0, 40);
  }, [products, productSearch]);

  const toggleProduct = (id) => {
    setForm((f) => {
      const s = new Set(f.applicable_product_ids || []);
      if (s.has(id)) s.delete(id); else s.add(id);
      return { ...f, applicable_product_ids: Array.from(s) };
    });
  };
  const productNameById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p.name || p.title || p.id;
    return m;
  }, [products]);

  const save = async () => {
    if (!form.code.match(/^[A-Za-z0-9_-]{3,32}$/)) return toast.error("Code must be 3-32 chars (A-Z, 0-9, _, -)");
    if (form.discount_type === "percent" && form.value > 100) return toast.error("Percent cannot exceed 100");
    try {
      const payload = { ...form, code: form.code.toUpperCase(), value: Number(form.value) };
      ["max_discount", "min_order", "usage_limit", "per_user_limit"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      if (!payload.start_at) delete payload.start_at;
      if (!payload.end_at) delete payload.end_at;
      if (editingId) await api.put(`/admin/coupons/${editingId}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editingId ? "Updated" : "Created");
      setForm(blank); setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const edit = (c) => { setForm({ ...blank, ...c }); setEditingId(c.id); };
  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
  };

  const columns = [
    { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
    { key: "discount_type", label: "Type", render: (r) => `${r.discount_type === "percent" ? `${r.value}%` : `₹${r.value}`}` },
    { key: "used_count", label: "Used", render: (r) => `${r.used_count || 0}${r.usage_limit ? `/${r.usage_limit}` : ""}` },
    { key: "min_order", label: "Min Order", render: (r) => r.min_order ? `₹${r.min_order}` : "—" },
    { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" },
    {
      key: "actions", label: "",
      render: (r) => (
        <div className="flex gap-2">
          <button onClick={() => edit(r)} className="text-xs underline" data-testid={`edit-coupon-${r.code}`}>Edit</button>
          <button onClick={() => remove(r.id)} className="text-xs text-red-700" data-testid={`delete-coupon-${r.code}`}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-coupons-page">
      <div className="overline mb-2">Marketing</div>
      <h1 className="font-display text-4xl mb-8">Coupons</h1>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-8">
        <div className="overline mb-4">{editingId ? "Edit Coupon" : "New Coupon"}</div>
        <div className="grid md:grid-cols-3 gap-3">
          <input className="kb-input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code" />
          <select className="kb-input" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="coupon-type">
            <option value="percent">Percent %</option>
            <option value="flat">Flat ₹</option>
          </select>
          <input type="number" className="kb-input" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-value" />
          <input type="number" className="kb-input" placeholder="Min Order (₹)" value={form.min_order ?? ""} onChange={(e) => setForm({ ...form, min_order: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Max Discount (₹)" value={form.max_discount ?? ""} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Usage Limit" value={form.usage_limit ?? ""} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
          <input type="date" className="kb-input" placeholder="Start" value={form.start_at || ""} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          <input type="date" className="kb-input" placeholder="End" value={form.end_at || ""} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          <label className="flex items-center gap-2 text-sm" data-testid="coupon-new-user-only-label">
            <input
              type="checkbox"
              checked={!!form.new_user_only}
              onChange={(e) => setForm({ ...form, new_user_only: e.target.checked })}
              data-testid="coupon-new-user-only"
            />
            New users only
          </label>
          <input
            type="number"
            className="kb-input"
            placeholder="Per-user limit"
            value={form.per_user_limit ?? ""}
            onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })}
            data-testid="coupon-per-user-limit"
          />
        </div>
        <input className="kb-input mt-3" placeholder="Description (optional)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />

        {/* Eligible products — leave empty to apply to the whole catalog.
            When any product is picked, the coupon only discounts those
            lines in the cart (server-side, coupons.py:57-71). */}
        <div className="mt-4 border border-[hsl(var(--kb-border))] rounded p-3" data-testid="coupon-products-picker">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Eligible Products</div>
            <div className="text-xs text-neutral-500">
              {form.applicable_product_ids?.length
                ? `${form.applicable_product_ids.length} selected`
                : "Empty = applies to all products"}
            </div>
          </div>
          {form.applicable_product_ids?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {form.applicable_product_ids.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 text-xs bg-neutral-100 border border-[hsl(var(--kb-border))] rounded px-2 py-0.5"
                  data-testid={`selected-product-${id}`}
                >
                  {productNameById[id] || id}
                  <button
                    type="button"
                    onClick={() => toggleProduct(id)}
                    className="text-neutral-500 hover:text-red-600"
                    aria-label="Remove"
                  >×</button>
                </span>
              ))}
            </div>
          )}
          <input
            className="kb-input mb-2"
            placeholder="Search products…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            data-testid="coupon-products-search"
          />
          <div className="max-h-40 overflow-y-auto text-sm">
            {visibleProducts.length === 0 && (
              <div className="text-xs text-neutral-500 py-2">
                {products.length === 0 ? "No products yet." : "No products match this search."}
              </div>
            )}
            {visibleProducts.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-2 py-1 cursor-pointer hover:bg-neutral-50 px-1"
                data-testid={`coupon-product-option-${p.id}`}
              >
                <input
                  type="checkbox"
                  checked={form.applicable_product_ids?.includes(p.id) || false}
                  onChange={() => toggleProduct(p.id)}
                />
                <span className="truncate">{p.name || p.title || p.id}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button className="kb-btn-primary" onClick={save} data-testid="save-coupon-btn">{editingId ? "Update" : "Create"}</button>
          {editingId && <button className="kb-btn-outline" onClick={() => { setForm(blank); setEditingId(null); }}>Cancel</button>}
        </div>
      </div>

      <DataTable columns={columns} rows={coupons} loading={loading} emptyLabel="No coupons" testid="coupons-table" />
    </div>
  );
}
